// import { CallToAction } from '@/components/CallToAction'
// import { Faqs } from '@/components/Faqs'
// import { Pricing } from '@/components/Pricing'

// import { SecondaryFeatures } from '@/components/SecondaryFeatures'
// import { Testimonials } from '@/components/Testimonials'
import React, { useState, useEffect, useRef, useContext } from 'react';
import { Footer } from '../../common/Footer';
import { Header } from '../../common/Header';
import { Hero } from '../../common/Hero';
import { PrimaryFeatures } from '../../common/PrimaryFeatures';
import { SecondaryFeatures } from '../../common/SecondaryFeatures';
import { Faqs } from '../../common/Faqs';
import { CallToAction } from '../../common/CallToAction';
import { Solutions } from '../../common/Testimonials';
import { Integrations } from '../../common/Pricing';
import { SocialProof } from '../../common/SocialProof';
import { WorkflowTransformation } from '../../common/WorkflowTransformation';
import DataFlowDiagram from '../../common/DataFlowDiagram';
import CookieConsent from '../../common/CookieConsent';
import ChatSupport from '../../common/ChatSupport';
import '../../../assets/styles/animations.css';
import { scrollToHashOnLoad } from '../../../utils/scrollUtils';
import { ChatSupportContext } from '../../../contexts/ChatSupportContext';

export default function LandingPagev2() {
  const [animatedSections, setAnimatedSections] = useState({
    features: true,
    socialProof: true,
    workflowTransformation: true,
    secondaryFeatures: true,
    dataFlow: true,
    solutions: true,
    integrations: true,
    faqs: true,
    callToAction: true
  });
  
  const sectionRefs = {
    features: useRef(),
    socialProof: useRef(),
    workflowTransformation: useRef(),
    secondaryFeatures: useRef(),
    dataFlow: useRef(),
    solutions: useRef(),
    integrations: useRef(),
    faqs: useRef(),
    callToAction: useRef()
  };
  
  // Get the chat support ref from context
  const { chatSupportRef } = useContext(ChatSupportContext);

  // Set up intersection observer to trigger animations on scroll
  useEffect(() => {
    // Set all sections to visible by default
    setAnimatedSections({
      features: true,
      socialProof: true,
      workflowTransformation: true,
      secondaryFeatures: true,
      dataFlow: true,
      solutions: true,
      integrations: true,
      faqs: true,
      callToAction: true
    });

    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setAnimatedSections(prev => ({
            ...prev,
            [entry.target.id]: true
          }));
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    
    // Observe each section
    Object.values(sectionRefs).forEach(ref => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    // Handle scrolling to section if URL has hash on load
    scrollToHashOnLoad(80);

    return () => {
      Object.values(sectionRefs).forEach(ref => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, []);

  return (
    <div className="flex flex-col min-h-screen w-full relative overflow-x-hidden bg-white">
      {/* Professional background elements */}
      <div className="absolute inset-0 pointer-events-none -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-white to-blue-50/10 opacity-70"></div>
      </div>
      
      <Header />
      <main className="flex-grow w-full">
        {/* Hero section takes up full viewport height */}
        <section className="min-h-[calc(100vh-80px)]">
          <Hero />
        </section>
        
        {/* Content sections start below full viewport */}
        <div 
          id="features"
          ref={sectionRefs.features}
          className="visible w-full"
        >
          <PrimaryFeatures />
        </div>

        <div 
          id="socialProof"
          ref={sectionRefs.socialProof}
          className="visible w-full"
        >
          <SocialProof />
        </div>
        
        <div 
          id="workflowTransformation"
          ref={sectionRefs.workflowTransformation}
          className="visible w-full"
        >
          <WorkflowTransformation />
        </div>
        
        <div 
          id="secondaryFeatures"
          ref={sectionRefs.secondaryFeatures}
          className="visible w-full"
        >
          <SecondaryFeatures />
        </div>
    
        
        <div 
          id="integrations"
          ref={sectionRefs.integrations}
          className="visible w-full"
        >
          <Integrations />
        </div>
        
        <div 
          id="faqs"
          ref={sectionRefs.faqs}
          className="visible w-full"
        >
          <Faqs />
        </div>
        
        <div 
          id="callToAction"
          ref={sectionRefs.callToAction}
          className="visible w-full"
        >
          <CallToAction />
        </div>
      </main>
      <Footer />
      
      {/* Add Cookie Consent Banner */}
      <CookieConsent />
      
      {/* Add Chat Support */}
      <ChatSupport ref={chatSupportRef} />
    </div>
  );
}
